// // ----------------------------------------------------------------
// // <copyright company='Microsoft Corporation'>
// //     Copyright (c) Microsoft Corporation.  All rights reserved.
// // </copyright>
// // ----------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
    ComboBox,
    Dialog,
    DialogFooter,
    DialogType,
    IComboBoxOption,
    IComboBoxStyles,
    Label,
    Spinner,
    SpinnerSize,
    TextField,
    IDropdownOption,
    Checkbox,
} from 'office-ui-fabric-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Message from './../../components/Messages';
import { actionStatusSelector, LoadingState } from './../../store/actionStatus';
import {
    POST_SOLD_TO_SHIP_TO,
    createSoldToShipToStatusSelector,
} from './../../store/managePartners';
import Styles from './managePartners.module.scss';
import { postalCodeValidation } from './../../helpers/postalCodePatternPerCountry';
import { provinceValidation } from './../../helpers/provinceRequiredPerCountry';
import { provincesSelector, getProvincesData } from './../../store/provinces';
import { validateProvince, validatePostalCode, validationByPattern, validationPatterns } from './../../helpers/validation';

const dropdownStyles: Partial<IComboBoxStyles> = {
    container: {
        minWidth: 100,
    },
    errorMessage: {
        fontSize: 12,
    },
    optionsContainerWrapper: {
        maxHeight: 350,
    },
};

interface ProvinceType {
    ISOCode: string | number;
    ISOName: string;
}

export interface SoldToShipToType {
    process?: string;
    soldToCustomerNbr?: string;
    shipToCustomerNbr?: string;
    email?: string;
    customerName?: string;
    countryCode?: string;
    countryName?: string;
    stateProvinceCode?: string;
    stateProvinceName?: string;
    addressLineOneText?: string;
    addressLineTwoText?: string;
    addressLineThreeText?: string;
    cityName?: string;
    postalCode?: string;
    isDummy?: boolean;
    isActive?: boolean;
}

const initialNewSoldToShipToState: SoldToShipToType = {
    process: 'ADD',
    soldToCustomerNbr: '',
    shipToCustomerNbr: '',
    customerName: '',
    countryCode: '',
    countryName: '',
    stateProvinceCode: '',
    stateProvinceName: '',
    addressLineOneText: '',
    addressLineTwoText: '',
    addressLineThreeText: '',
    cityName: '',
    postalCode: '',
    isDummy: false,
    isActive: false,
};

const initialValidationState = {
    soldToCustomerNbr: undefined,
    shipToCustomerNbr: undefined,
    customerName: undefined,
    countryCode: undefined,
    email: undefined,
    stateProvinceCode: undefined,
    addressLineOneText: undefined,
    cityName: undefined,
    postalCode: undefined,
};

const initialCountryInfo = {
    countryCode: undefined,
    countryName: undefined,
    countryShortCode: undefined,
};
export interface ManageAddressPropTypes {
    hide: boolean;
    onCloseOrOpen: (flag: boolean) => void;
    soldToShipTo?: SoldToShipToType;
    title: string;
    soldToList: any;
    countriesList: any;
    isSoldToAction: boolean;
    isShipToAction: boolean;
    onSaveClicked: (soldToShipToInputs: SoldToShipToType) => void;
}

const MAX_CHARACTER_LIMIT = 35;
export default function AddOrUpdate(props: ManageAddressPropTypes) {

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [hide, setHideDialog] = useState(props.hide);
    const [postalCodeRequired, setPostalCodeRequired] = useState(false);
    const [provinceRequired, setProvinceRequired] = useState(false);
    const [soldToShipToDetails, setSoldToShipToDetails] = useState<SoldToShipToType>(initialNewSoldToShipToState);
    const [validation, setValidation] = useState<any>(initialValidationState);

    const [submitted, setSubmitted] = useState(false);
    const createStatus = useSelector(state => actionStatusSelector(state)(POST_SOLD_TO_SHIP_TO));
    const isCreatingUpdatingUser = createStatus.status === LoadingState.started;
    const partnerCreationResponse = useSelector(state => createSoldToShipToStatusSelector(state));

    const [soldToCustomerNbr, setSoldToCustomerNbr] = useState<any>(undefined);
    const [selectedSoldTo, setSelectedSoldTo] = useState<any>(undefined);
    const [shipToCustomerNbr, setShipToCustomerNbr] = useState<any>(undefined);
    const [customerName, setCustomerName] = useState<any>(undefined);
    const [countryCode, setCountryCode] = useState<any>(undefined);
    const [email, setEmail] = useState<any>(undefined);
    const [countryInfo, setCountryInfo] = React.useState<any>(initialCountryInfo);
    const [stateProvinceCode, setStateProvinceCode] = useState<any>(undefined);
    const [addressLineOneText, setAddressLineOneText] = useState<any>(undefined);
    const [addressLineTwoText, setAddressLineTwoText] = useState<any>(undefined);
    const [addressLineThreeText, setAddressLineThreeText] = useState<any>(undefined);
    const [cityName, setCityName] = useState<any>(undefined);
    const [postalCode, setPostalCode] = useState<any>(undefined);
    const [isDummy, setIsDummy] = useState(false);

    const soldToList = React.useMemo(() => {
        return props.soldToList.sort().map(
            (option: { soldToCustomerNbr: string | number, customerName: string }) =>
            ({
                key: option.soldToCustomerNbr,
                text: `${option.customerName} - ${option.soldToCustomerNbr}`,
            } as IDropdownOption)
        );
    }, [props.countriesList]);
    const [soldToListOptions, setSoldToListOptions] = useState(soldToList);

    const countryList = React.useMemo(() => {
        return props.countriesList.sort().map(
            (option: { ISOCode: string; ISOCountryShortCode: string | number; ISOName: string }) =>
            ({
                key: option.ISOCode, //as there is duplication of ISOCountryShortCode, using the ISO code for key value
                text: option.ISOName,
                data: option.ISOCountryShortCode,
            } as IDropdownOption)
        );
    }, [props.countriesList]);
    const [countryOptions, setCountryOptions] = useState(countryList);

    const provinces = useSelector((state: any) =>
        provincesSelector(state)(
            (countryCode || '').toLocaleLowerCase()
        )
    );

    const provincesList = React.useMemo(() => {
        return (
            (provinces ||
                [] as ProvinceType[]).sort().map(
                    (option: { ISOCode: string | number; ISOName: string }) =>
                    ({
                        key: option.ISOCode,
                        text: option.ISOName,
                    } as IDropdownOption)
                )
        );
    }, [provinces]);

    const [provincesOptions, setProvincesOptions] = useState([]);

    useEffect(() => {
        setProvincesOptions(provincesList);
    }, [provincesList]);

    function validateStateOrProvince(province?: string) {
        return provinceRequired ? ((soldToShipToDetails.countryCode && province && validateProvince(soldToShipToDetails.countryCode, province)) ? ''
            : t('required_message', { fieldName: t('state_province') })) : '';
    }

    function validateZipCode(value?: any) {
        if (!value) {
            return t('required_message', { fieldName: t('zip_code') });
        }
        if (!validatePostalCode(soldToShipToDetails.countryCode, value)) {
            return t('invalid_postal_code');
        }
        return '';
    }

    function validateEmail(value: any) {
        const { email } = validationPatterns;

        if (!validationByPattern(email, value)) {
            return t('invalid_email_address');
        }
        return '';
    }

    function isProvinceRequired(countryCode: any) {
        const provinceRequired = countryCode && provinceValidation[countryCode.toLowerCase()];
        if (provinceRequired && provinceRequired.required) {
            setProvinceRequired(true);
        } else {
            setProvinceRequired(false);
        }

    }

    function isValid() {
        setValidation(initialValidationState);
        const errors = {
            soldToCustomerNbr:
                soldToShipToDetails.soldToCustomerNbr?.trim() ?
                    props.soldToShipTo?.process === 'ADD' && props.isSoldToAction ?
                        soldToListOptions.filter((soldTo: any) => soldTo.key === soldToShipToDetails.soldToCustomerNbr?.trim()).length > 0 ? t('sold_to_exists') : ''
                        : ''
                    : t('required_message', { fieldName: t('sold_to_customer_nbr') }),
            shipToCustomerNbr: props.isSoldToAction || soldToShipToDetails.shipToCustomerNbr?.trim() ? '' : t('required_message', { fieldName: t('ship_to_customer_number') }),
            email: validateEmail(soldToShipToDetails?.email?.trim()),
            customerName: soldToShipToDetails.customerName?.trim() ? '' : t('required_message', { fieldName: t('partner_name') }),
            countryCode: soldToShipToDetails.countryCode?.trim() ? '' : t('required_message', { fieldName: t('country') }),
            addressLineOneText: soldToShipToDetails.addressLineOneText?.trim() ? '' : t('required_message', { fieldName: t('address_1') }),
            cityName: soldToShipToDetails.cityName?.trim() ? '' : t('required_message', { fieldName: t('city') }),
            stateProvinceCode: validateStateOrProvince(soldToShipToDetails.stateProvinceCode?.trim()),
            postalCode: validateZipCode(soldToShipToDetails.postalCode?.trim()),
        };
        setValidation(errors);
        return !Object.keys(errors).some(key => errors[key]);
    }

    function onClickClose(evnt: any) {
        const close = true;
        setHideDialog(close);
        if (props.onCloseOrOpen) {
            props.onCloseOrOpen(!close);
        }
        setSubmitted(false);
        setCountryOptions(countryList);
        setSoldToListOptions(soldToList);
        setCountryInfo(initialCountryInfo);
    }

    function onClickSave() {
        if (!isValid()) {
            return;
        }
        props.onSaveClicked(soldToShipToDetails);
        //dispatch(postSoldToShipTo({ soldToShipToDetails }));
        setValidation(initialValidationState);
        setSubmitted(true);
    }

    function countryOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            // const selectedCountryShortCode = selectedOption.data as string;
            // const selectedCountryName = selectedOption.text as string;
            // const selectedCountryCode = selectedOption.key as string;
            const { key, text, data } = selectedOption;

            setSoldToShipToDetails((prevState: any) => ({ ...prevState, countryCode: data }));
            setCountryCode(data);
            setSoldToShipToDetails((prevState: any) => ({ ...prevState, countryName: text }));
            setCountryInfo({ countryCode: key.toString(), countryName: text.toString(), countryShortCode: data });
            setStateEmpty();
            const postalCodeRequired =
                postalCodeValidation[data.toLowerCase()];
            if (postalCodeRequired) {
                setPostalCodeRequired(true);
            } else {
                setPostalCodeRequired(false);
            }

            isProvinceRequired(data);
        } else {
            setCountryCode('');
            setSoldToShipToDetails((prevState: any) => ({ ...prevState, countryCode: '' }));
            setSoldToShipToDetails((prevState: any) => ({ ...prevState, countryName: '' }));
            setStateEmpty();
        }
    }

    function onPendingValueChangedCountry(selectedOption?: IComboBoxOption, pendingIndex?: number, pendingValue?: string) {
        if ((typeof (pendingValue) === 'undefined' && typeof (selectedOption) === 'undefined') || (typeof (selectedOption) !== 'undefined')) {
            return;
        }

        if (typeof (pendingValue) !== 'undefined') {
            const filteredCountryList = countryList.filter((countryObj: any) => (countryObj.text).toLowerCase().startsWith(pendingValue.toLowerCase()));
            setCountryOptions(filteredCountryList);
        }
    }

    function provinceOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            const selectedStateCode = (selectedOption.key as unknown) as string;
            const selectedStateName = (selectedOption.text as unknown) as string;
            setSoldToShipToDetails((prevState: any) => ({ ...prevState, stateProvinceCode: selectedStateCode }));
            setStateProvinceCode(selectedStateCode);
            setSoldToShipToDetails((prevState: any) => ({ ...prevState, stateProvinceName: selectedStateName }));
        } else {
            setStateEmpty();
        }
    }

    function setStateEmpty() {
        setStateProvinceCode('');
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, stateProvinceCode: '' }));
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, stateProvinceName: '' }));
    }

    function onPendingValueChangedProvince(selectedOption?: IComboBoxOption, pendingIndex?: number, pendingValue?: string) {
        if ((typeof (pendingValue) === 'undefined' && typeof (selectedOption) === 'undefined') || (typeof (selectedOption) !== 'undefined')) {
            return;
        }

        if (typeof (pendingValue) !== 'undefined') {
            const filteredProvincesList = provincesList.filter((cprovinceObj: any) => (cprovinceObj.text).toLowerCase().startsWith(pendingValue.toLowerCase()));
            setProvincesOptions(filteredProvincesList);
        }
    }

    function onChangeSoldTo(event: any, selectedOption?: IComboBoxOption) {
        if (selectedOption) {
            const selectedSoldTo = (selectedOption.key as unknown) as string;
            setSoldToShipToDetails((prevState: any) => ({ ...prevState, soldToCustomerNbr: selectedSoldTo }));
            setSoldToCustomerNbr(selectedSoldTo);
            setSelectedSoldTo(selectedSoldTo);
        }
    }

    function onPendingValueChangedSoldTo(selectedOption?: IComboBoxOption, pendingIndex?: number, pendingValue?: string) {
        if ((typeof (pendingValue) === 'undefined' && typeof (selectedOption) === 'undefined') || (typeof (selectedOption) !== 'undefined')) {
            return;
        }

        if (typeof (pendingValue) !== 'undefined') {
            const filteredSoldToList = soldToList.filter((soldTo: any) => (soldTo.text).toLowerCase().startsWith(pendingValue.toLowerCase()));
            setSoldToListOptions(filteredSoldToList);
        }
    }

    function onChangeSoldToCustomerNbr(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, soldToCustomerNbr: newValue }));
        setSoldToCustomerNbr(newValue);
    }

    function onChangeShipToCustomerNbr(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, shipToCustomerNbr: newValue }));
        setShipToCustomerNbr(newValue);
    }

    function onChangeCustomerName(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, customerName: newValue }));
        setCustomerName(newValue);
    }

    function onChangeEmailAddress(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, email: newValue }));
        setEmail(newValue);
    }

    function onChangeAddressLineOneText(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, addressLineOneText: newValue }));
        setAddressLineOneText(newValue);
    }

    function onChangeAddressLineTwoText(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, addressLineTwoText: newValue }));
        setAddressLineTwoText(newValue);
    }

    function onChangeAddressLineThreeText(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, addressLineThreeText: newValue }));
        setAddressLineThreeText(newValue);
    }

    function onChangeCityName(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, cityName: newValue }));
        setCityName(newValue);
    }

    function onChangePostalCode(event: any, newValue?: string) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, postalCode: newValue }));
        setPostalCode(newValue);
    }

    function onChangeIsDummy(event: any, checked?: boolean) {
        setSoldToShipToDetails((prevState: any) => ({ ...prevState, isDummy: checked || false }));
        setIsDummy(checked || false);
    }

    const errorMessage = submitted
        && partnerCreationResponse.status
        && partnerCreationResponse.status !== 'succeeded' ?
        `${t('error_occurred')} ${partnerCreationResponse && partnerCreationResponse.status}` : undefined;

    useEffect(() => {
        setHideDialog(props.hide);
        if (!props.hide) {
            setValidation(initialValidationState);
        }
    }, [props.hide]);

    //Close the dialog after successfully saving a record
    useEffect(() => {
        if (partnerCreationResponse.status && partnerCreationResponse.status.toLowerCase() === 'succeeded') {
            const close = true;
            setHideDialog(close);
            if (props.onCloseOrOpen) {
                props.onCloseOrOpen(!close);
            }
        }
    }, [partnerCreationResponse.status]);

    if (props.soldToShipTo) {
        useEffect(() => {
            if (props.soldToShipTo) {
                setSoldToShipToDetails(props.soldToShipTo);
                setSoldToShipToDetails({ ...props.soldToShipTo, process: props.soldToShipTo.process });
                setSoldToShipToDetails({ ...props.soldToShipTo, countryName: props.soldToShipTo.countryName });
                setSoldToShipToDetails({ ...props.soldToShipTo, stateProvinceName: props.soldToShipTo.stateProvinceName });
                if (props.isShipToAction) {
                    setSelectedSoldTo(props.soldToShipTo.soldToCustomerNbr);
                }
                else {
                    setSoldToCustomerNbr(props.soldToShipTo.soldToCustomerNbr);
                }
                setShipToCustomerNbr(props.soldToShipTo.shipToCustomerNbr);
                setCustomerName(props.soldToShipTo.customerName);
                setEmail(props?.soldToShipTo?.email);
                setCountryCode(props?.soldToShipTo?.countryCode?.trim());
                const countryDetails = countryList.find((country: any) => country.data === props?.soldToShipTo?.countryCode?.trim());
                setCountryInfo({ countryCode: countryDetails?.key, countryName: props.soldToShipTo.countryName, countryShortCode: props.soldToShipTo.countryCode?.trim() });
                setStateProvinceCode(props?.soldToShipTo?.stateProvinceCode?.trim());
                setAddressLineOneText(props.soldToShipTo.addressLineOneText);
                setAddressLineTwoText(props.soldToShipTo.addressLineTwoText);
                setAddressLineThreeText(props.soldToShipTo.addressLineThreeText);
                setCityName(props.soldToShipTo.cityName);
                setPostalCode(props.soldToShipTo.postalCode);
                setIsDummy(props.soldToShipTo.isDummy ? props.soldToShipTo.isDummy : false);
                setSoldToShipToDetails(prevState => ({ ...prevState }));
                isProvinceRequired(props?.soldToShipTo?.countryCode?.trim());
            }
        }, [props.soldToShipTo]);
    }

    useEffect(() => {
        dispatch(getProvincesData());
    }, []);

    return (<section>
        <Dialog
            minWidth="550px"
            hidden={hide}
            onDismiss={onClickClose}
            dialogContentProps={{
                titleId: 'add-new-edit-popup',
                type: DialogType.normal,
                closeButtonAriaLabel: t('close'),
                title: t(props.title),
                showCloseButton: true,
                onDismiss: onClickClose,
            }}
            modalProps={{
                titleAriaId: 'add-new-edit-popup',
                isBlocking: true,
                containerClassName: `${Styles.popup}`,
            }}
        >
            <div className="ms-Grid">
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm12')}>
                        <Message errorMessage={errorMessage} />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow, props.isSoldToAction === false && props.isShipToAction === true ? '' : Styles.hidden)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="cmb-soldto-name">{t('add_sold_to_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <ComboBox
                            ariaLabel={t('sold_to_name')}
                            autoComplete="off"
                            allowFreeform={true}
                            errorMessage={validation.soldToCustomerNbr}
                            id="cmb-soldto-name"
                            onChange={onChangeSoldTo}
                            onPendingValueChanged={onPendingValueChangedSoldTo}
                            options={soldToListOptions}
                            placeholder={t('select_sold_to')}
                            required={true}
                            selectedKey={selectedSoldTo}
                            styles={dropdownStyles}
                            openOnKeyboardFocus={true}
                            useComboBoxAsMenuWidth={true}
                        />
                    </div>
                </div>

                <div className={clsx('ms-Grid-row', Styles.addEditRow, props.isSoldToAction === true && props.isShipToAction === false ? '' : Styles.hidden)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-soldto-num" >{t('add_sold_to_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('sold_to_name')}
                            errorMessage={validation.soldToCustomerNbr}
                            id="txt-soldto-num"
                            name="soldToNum"
                            onChange={onChangeSoldToCustomerNbr}
                            value={soldToCustomerNbr}
                        />
                    </div>
                </div>

                <div className={clsx('ms-Grid-row', Styles.addEditRow, props.isSoldToAction === false && props.isShipToAction === true ? '' : Styles.hidden)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-shipto-num" >{t('add_ship_to_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('ship_to_name')}
                            errorMessage={validation.shipToCustomerNbr}
                            id="txt-shipto-num"
                            name="shipToNum"
                            onChange={onChangeShipToCustomerNbr}
                            value={shipToCustomerNbr}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-customer-name" >
                            {props.isSoldToAction === true && props.isShipToAction === false ? t('add_sold_to_partner_name') : t('add_ship_to_partner_name')}
                        </Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={props.isSoldToAction === true && props.isShipToAction === false ? t('add_sold_to_partner_name') : t('add_ship_to_partner_name')}
                            errorMessage={validation.customerName}
                            id="txt-customer-name"
                            name="customerName"
                            onChange={onChangeCustomerName}
                            value={customerName}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label)} htmlFor="txt-email-address" >{t('partner_email')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('email')}
                            errorMessage={validation.email}
                            id="txt-email-address"
                            name="email"
                            onChange={onChangeEmailAddress}
                            value={email}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-country-code" >{t('add_country_code')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <ComboBox
                            autoComplete="off"
                            ariaLabel={t('country')}
                            //disabled={props.disabled || props.readonly}
                            selectedKey={countryInfo.countryCode}
                            errorMessage={validation.countryCode}
                            required={true}
                            options={countryOptions}
                            styles={dropdownStyles}
                            onChange={countryOnChange}
                            allowFreeform={true}
                            onPendingValueChanged={onPendingValueChangedCountry}
                            openOnKeyboardFocus={true}
                            useComboBoxAsMenuWidth={true}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-address-lineone" >{t('add_address_line_1')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('add_address_line_1')}
                            errorMessage={validation.addressLineOneText}
                            id="txt-address-lineone"
                            name="addressLineOneText"
                            onChange={onChangeAddressLineOneText}
                            value={addressLineOneText}
                            maxLength={MAX_CHARACTER_LIMIT}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label)} htmlFor="txt-address-linetwo" >{t('add_address_line_2')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('add_address_line_2')}
                            errorMessage={validation.addressLineTwoText}
                            id="txt-address-linetwo"
                            name="addressLineTwoText"
                            onChange={onChangeAddressLineTwoText}
                            value={addressLineTwoText}
                            maxLength={MAX_CHARACTER_LIMIT}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label)} htmlFor="txt-address-linethree" >{t('add_address_line_3')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('add_address_line_3')}
                            errorMessage={validation.addressLineThreeText}
                            id="txt-address-linethree"
                            name="addressLineThreeText"
                            onChange={onChangeAddressLineThreeText}
                            value={addressLineThreeText}
                            maxLength={MAX_CHARACTER_LIMIT}
                        />
                    </div>
                </div>
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-city-name" >{t('add_city_name')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('add_city_name')}
                            errorMessage={validation.cityName}
                            id="txt-city-name"
                            name="cityName"
                            onChange={onChangeCityName}
                            value={cityName}
                            maxLength={MAX_CHARACTER_LIMIT}
                        />
                    </div>
                </div>
                {provinceRequired ?
                    <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                        <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                            <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-state-code" >{t('add_state_province_code')}</Label>
                        </div>
                        <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                            <ComboBox
                                autoComplete="off"
                                ariaLabel={t('add_state_province_code')}
                                errorMessage={validation.stateProvinceCode}
                                selectedKey={stateProvinceCode}
                                onChange={provinceOnChange}
                                options={provincesOptions}
                                required={provinceRequired}
                                styles={dropdownStyles}
                                allowFreeform={true}
                                onPendingValueChanged={onPendingValueChangedProvince}
                                openOnKeyboardFocus={true}
                                useComboBoxAsMenuWidth={true}
                            />
                        </div>
                    </div>
                    : ''}
                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label, Styles.labelRequired)} htmlFor="txt-postal-code" >{t('add_postal_code')}</Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <TextField
                            ariaLabel={t('add_postal_code')}
                            errorMessage={validation.postalCode}
                            id="txt-postal-code"
                            name="postalCode"
                            onChange={onChangePostalCode}
                            value={postalCode}
                            required={postalCodeRequired}
                        />
                    </div>
                </div>

                <div className={clsx('ms-Grid-row', Styles.addEditRow)}>
                    <div className={clsx('ms-Grid-col', 'ms-sm5')}>
                        <Label className={clsx(Styles.label)} htmlFor="chk-isdummy">
                            {props.isSoldToAction === true && props.isShipToAction === false ? t('isdummy_soldto') : t('isdummy_shipto')}
                        </Label>
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm7')}>
                        <Checkbox
                            ariaLabel={t('isdummy_soldtoshipto')}
                            id="chk-isdummy"
                            checked={isDummy}
                            onChange={onChangeIsDummy}
                        />
                    </div>
                </div>
            </div>
            <DialogFooter>
                <button className={clsx(Styles.buttonPrimary, Styles.button, Styles.buttonSave)} disabled={isCreatingUpdatingUser} onClick={onClickSave} >{t('Save')}</button>
                {
                    isCreatingUpdatingUser && (
                        <Spinner
                            className={Styles.spinnerStyle}
                            labelPosition="top"
                            size={SpinnerSize.medium}
                        />
                    )}
                <button className={clsx(Styles.buttonSecondary, Styles.button, Styles.buttonCancel)} onClick={onClickClose}>{t('cancel')}
                </button>
            </DialogFooter>
        </Dialog>

    </section>);

}
